import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { DateOnlyField } from '../ui/DateOnlyField';
import { useAuth } from '../../contexts/AuthContext';
import {
  CERTIFICATE_LIBRARY_TYPE_LABEL,
  PRODUCT_TYPES,
  deactivateCertificateFile,
  findLibraryRow,
  getCertificateSignedUrl,
  isCertificateExpired,
  isCertificateUnavailable,
  listCertificateFiles,
  markCertificateUnavailable,
  resolveMaterialKeys,
  uploadCertificateFile,
  type CertificateFileRow,
  type ProductCertificateType,
} from '../../lib/certificateLibrary';
import {
  searchCertificateItems,
  type CertificateItemLookup,
} from '../../lib/certificateRequestLookup';
import type { CatalogueItem } from '../../lib/catalogueItems';
import { singaporeTodayDateString } from '../../utils/dateTime';

type EditorMode = 'closed' | 'upload' | 'unavailable';

type SlotRow = {
  materialKey: string;
  materialLabel: string;
  cert_type: ProductCertificateType;
  row: CertificateFileRow | null;
};

type Props = {
  item: CatalogueItem | null;
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
};

function addYearsToIsoDate(isoDate: string, years: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!m) return '';
  const y = Number(m[1]) + years;
  const month = m[2];
  const day = Number(m[3]);
  // Clamp Feb 29 → Feb 28 on non-leap targets.
  const lastDay = new Date(Date.UTC(y, Number(month), 0)).getUTCDate();
  const safeDay = Math.min(day, lastDay);
  return `${y}-${month}-${String(safeDay).padStart(2, '0')}`;
}

function formatValidity(row: CertificateFileRow): string {
  if (isCertificateUnavailable(row)) return '—';
  const from = row.valid_from?.trim();
  const until = row.valid_until?.trim() || '—';
  return from ? `${from} → ${until}` : until;
}

function slotStatus(row: CertificateFileRow | null): {
  pill: string;
  label: string;
} {
  if (!row) return { pill: 'missing', label: 'Missing' };
  if (isCertificateUnavailable(row)) return { pill: 'missing', label: 'Not available' };
  if (isCertificateExpired(row.valid_until)) return { pill: 'expired', label: 'Expired' };
  return { pill: 'ok', label: 'Ready' };
}

export function PurchasingItemCertModal({ item, open, onClose, onChanged }: Props) {
  const { profile, authUser } = useAuth();
  const uploader =
    (profile as { display_name?: string; username?: string } | null)?.display_name?.trim() ||
    (profile as { username?: string } | null)?.username?.trim() ||
    authUser?.email?.trim() ||
    null;

  const [lookup, setLookup] = useState<CertificateItemLookup | null>(null);
  const [library, setLibrary] = useState<CertificateFileRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewBusyId, setPreviewBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [editorMode, setEditorMode] = useState<EditorMode>('closed');
  const [editSlot, setEditSlot] = useState<SlotRow | null>(null);
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [naReason, setNaReason] = useState('');
  const [collapsedTypes, setCollapsedTypes] = useState<Record<string, boolean>>({});

  const masterVendor = lookup?.vendor_no?.trim() || '';
  const masterMaterialRaw = lookup?.material?.trim() || '';
  const packingSize = lookup?.packing?.trim() || item?.packing?.trim() || '';
  const ctnMeasurement = lookup?.ctn_measurement?.trim() || '';
  const materialSlots = useMemo(
    () => resolveMaterialKeys(masterMaterialRaw),
    [masterMaterialRaw],
  );

  const slotRows: SlotRow[] = useMemo(() => {
    if (!masterVendor || !materialSlots.length) return [];
    const rows: SlotRow[] = [];
    for (const mk of materialSlots) {
      for (const certType of PRODUCT_TYPES) {
        rows.push({
          materialKey: mk.key,
          materialLabel: mk.label,
          cert_type: certType,
          row: findLibraryRow(library, certType, masterVendor, mk.key) ?? null,
        });
      }
    }
    return rows;
  }, [library, masterVendor, materialSlots]);

  const load = useCallback(async () => {
    if (!item) return;
    setLoading(true);
    setError(null);
    try {
      const [all, matches] = await Promise.all([
        listCertificateFiles({ activeOnly: true }),
        searchCertificateItems(item.code, 10).catch(() => [] as CertificateItemLookup[]),
      ]);
      const exact =
        matches.find((m) => m.code.trim().toLowerCase() === item.code.trim().toLowerCase()) ??
        matches[0] ??
        null;
      setLookup(exact);
      setLibrary(all);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load certificates.');
    } finally {
      setLoading(false);
    }
  }, [item]);

  useEffect(() => {
    if (!open || !item) return;
    setMessage(null);
    setError(null);
    setEditorMode('closed');
    setEditSlot(null);
    setFile(null);
    setNaReason('');
    void load();
  }, [open, item, load]);

  if (!item) return null;

  function closeEditor() {
    setEditorMode('closed');
    setEditSlot(null);
    setFile(null);
    setNaReason('');
  }

  function openUpload(slot: SlotRow) {
    setEditorMode('upload');
    setEditSlot(slot);
    const from = slot.row?.valid_from?.trim() || singaporeTodayDateString();
    const until =
      slot.row?.valid_until?.trim() || addYearsToIsoDate(from, 5) || singaporeTodayDateString();
    setValidFrom(from);
    setValidUntil(until);
    setFile(null);
    setError(null);
    setMessage(null);
  }

  function onValidFromChange(nextFrom: string) {
    setValidFrom(nextFrom);
    if (!nextFrom.trim()) return;
    const autoUntil = addYearsToIsoDate(nextFrom, 5);
    if (autoUntil) setValidUntil(autoUntil);
  }

  function openUnavailable(slot: SlotRow) {
    setEditorMode('unavailable');
    setEditSlot(slot);
    setNaReason(slot.row?.unavailable_reason ?? '');
    setError(null);
    setMessage(null);
  }

  async function preview(row: CertificateFileRow) {
    if (!row.storage_path) return;
    setPreviewBusyId(row.id);
    setError(null);
    try {
      const url = await getCertificateSignedUrl(row.storage_path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to open preview.');
    } finally {
      setPreviewBusyId(null);
    }
  }

  async function saveUpload() {
    if (!editSlot || !item) return;
    if (!file) {
      setError('Choose a PDF to upload.');
      return;
    }
    if (!validUntil.trim()) {
      setError('Validity end date is required.');
      return;
    }
    if (!masterVendor) {
      setError('This item has no vendor on the item master.');
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await uploadCertificateFile({
        cert_type: editSlot.cert_type,
        item_no: item.code,
        vendor_no: masterVendor,
        material: editSlot.materialKey,
        valid_from: validFrom || null,
        valid_until: validUntil,
        file,
        uploaded_by: uploader,
      });
      closeEditor();
      await load();
      onChanged?.();
      setMessage('Certificate saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function saveUnavailable() {
    if (!editSlot || !item) return;
    if (!masterVendor) {
      setError('This item has no vendor on the item master.');
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await markCertificateUnavailable({
        cert_type: editSlot.cert_type,
        item_no: item.code,
        vendor_no: masterVendor,
        material: editSlot.materialKey,
        reason: naReason,
        uploaded_by: uploader,
      });
      closeEditor();
      await load();
      onChanged?.();
      setMessage('Marked as not available.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: CertificateFileRow) {
    if (!window.confirm('Remove this library entry?')) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await deactivateCertificateFile(row.id);
      if (editSlot?.row?.id === row.id) closeEditor();
      await load();
      onChanged?.();
      setMessage('Removed.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Remove failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    <Modal
      title="Item Details"
      open={open}
      onClose={onClose}
      extraWide
      compactHeader
      panelClassName="purch-cert-modal"
    >
      <div className="purch-cert-modal__body">
        <dl className="purch-cert-modal__meta">
          <div className="purch-cert-modal__meta-row">
            <dt>Item code</dt>
            <dd>{item.code}</dd>
          </div>
          <div className="purch-cert-modal__meta-row">
            <dt>Description</dt>
            <dd>{item.description || '—'}</dd>
          </div>
          <div className="purch-cert-modal__meta-row">
            <dt>Vendor</dt>
            <dd>{masterVendor || '—'}</dd>
          </div>
          <div className="purch-cert-modal__meta-row">
            <dt>Material</dt>
            <dd>{masterMaterialRaw || '—'}</dd>
          </div>
          <div className="purch-cert-modal__meta-row">
            <dt>CTN Measurement</dt>
            <dd>{ctnMeasurement || '—'}</dd>
          </div>
          <div className="purch-cert-modal__meta-row">
            <dt>Packing size</dt>
            <dd>{packingSize || '—'}</dd>
          </div>
        </dl>

        {error ? <p className="alert alert-error">{error}</p> : null}
        {message ? <p className="alert alert-success">{message}</p> : null}
        {loading ? <p className="page-lead">Loading…</p> : null}

        {!loading && (!masterVendor || !materialSlots.length) ? (
          <p className="purch-cert-modal__empty">
            This item needs vendor + material on the item master before certificates can be managed.
          </p>
        ) : null}

        {!loading && masterVendor && materialSlots.length > 0
          ? PRODUCT_TYPES.map((certType) => {
              const typeSlots = slotRows.filter((s) => s.cert_type === certType);
              const collapsed = Boolean(collapsedTypes[certType]);
              return (
                <section key={certType} className="purch-cert-modal__type-block">
                  <button
                    type="button"
                    className="purch-cert-modal__type-toggle"
                    aria-expanded={!collapsed}
                    onClick={() =>
                      setCollapsedTypes((prev) => ({
                        ...prev,
                        [certType]: !prev[certType],
                      }))
                    }
                  >
                    <span className="purch-cert-modal__type-chevron" aria-hidden>
                      {collapsed ? '▸' : '▾'}
                    </span>
                    <span className="purch-cert-modal__type-title">
                      {CERTIFICATE_LIBRARY_TYPE_LABEL[certType]}
                    </span>
                  </button>
                  {!collapsed ? (
                  <div className="purch-cert-modal__table-wrap table-wrap">
                    <table className="data-table purch-cert-modal__table">
                      <colgroup>
                        <col className="purch-cert-modal__col--material" />
                        <col className="purch-cert-modal__col--validity" />
                        <col className="purch-cert-modal__col--file" />
                        <col className="purch-cert-modal__col--status" />
                        <col className="purch-cert-modal__col--actions" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Material</th>
                          <th>Validity</th>
                          <th>File / reason</th>
                          <th>Status</th>
                          <th className="purch-cert-modal__actions-col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {typeSlots.map((slot) => {
                          const status = slotStatus(slot.row);
                          const hasFile =
                            Boolean(slot.row?.storage_path) &&
                            Boolean(slot.row?.file_name) &&
                            !isCertificateUnavailable(slot.row);
                          return (
                            <tr key={`${slot.materialKey}|${slot.cert_type}`}>
                              <td>
                                <strong>{slot.materialLabel}</strong>
                              </td>
                              <td>
                                {slot.row && !isCertificateUnavailable(slot.row)
                                  ? formatValidity(slot.row)
                                  : '—'}
                              </td>
                              <td>
                                {slot.row && isCertificateUnavailable(slot.row) ? (
                                  <span className="purch-cert-modal__na-reason">
                                    {slot.row.unavailable_reason}
                                  </span>
                                ) : hasFile ? (
                                  <button
                                    type="button"
                                    className="purch-cert-modal__file-link"
                                    disabled={previewBusyId === slot.row!.id}
                                    onClick={() => void preview(slot.row!)}
                                  >
                                    {previewBusyId === slot.row!.id
                                      ? 'Opening…'
                                      : slot.row!.file_name}
                                  </button>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>
                                <span
                                  className={`ctr-cert-lib__pill ctr-cert-lib__pill--${status.pill}`}
                                >
                                  {status.label}
                                </span>
                              </td>
                              <td>
                                <div className="purch-cert-modal__row-actions purch-cert-modal__row-actions--icons">
                                  <button
                                    type="button"
                                    className="purch-cert-modal__icon-btn"
                                    disabled={busy}
                                    title={hasFile ? 'Update PDF' : 'Upload PDF'}
                                    aria-label={hasFile ? 'Update PDF' : 'Upload PDF'}
                                    onClick={() => openUpload(slot)}
                                  >
                                    <span aria-hidden>{hasFile ? '↻' : '↑'}</span>
                                  </button>
                                  <button
                                    type="button"
                                    className="purch-cert-modal__icon-btn"
                                    disabled={busy}
                                    title="Not available"
                                    aria-label="Not available"
                                    onClick={() => openUnavailable(slot)}
                                  >
                                    <span aria-hidden>⊘</span>
                                  </button>
                                  {slot.row ? (
                                    <button
                                      type="button"
                                      className="purch-cert-modal__icon-btn purch-cert-modal__icon-btn--danger"
                                      disabled={busy}
                                      title="Remove"
                                      aria-label="Remove"
                                      onClick={() => void remove(slot.row!)}
                                    >
                                      <span aria-hidden>×</span>
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  ) : null}
                </section>
              );
            })
          : null}
      </div>
    </Modal>

      <Modal
        title={
          editSlot
            ? `${editSlot.row && !isCertificateUnavailable(editSlot.row) ? 'Update' : 'Upload'} · ${editSlot.materialLabel} · ${CERTIFICATE_LIBRARY_TYPE_LABEL[editSlot.cert_type]}`
            : 'Upload certificate'
        }
        open={editorMode === 'upload' && Boolean(editSlot)}
        onClose={closeEditor}
        nested
        compactHeader
        panelClassName="purch-cert-editor-modal"
      >
        <div className="purch-cert-editor">
          <p className="purch-cert-editor__hint">
            Valid until defaults to Valid from + 5 years — you can still change it.
          </p>
          <div className="purch-cert-editor__grid">
            <label className="field">
              <span className="field-label">Valid from</span>
              <DateOnlyField
                value={validFrom}
                disabled={busy}
                onChange={onValidFromChange}
              />
            </label>
            <label className="field">
              <span className="field-label">Valid until</span>
              <DateOnlyField
                value={validUntil}
                disabled={busy}
                onChange={setValidUntil}
              />
            </label>
            <label className="field purch-cert-editor__file">
              <span className="field-label">PDF</span>
              <div className="purch-cert-editor__file-box">
                <input
                  type="file"
                  className="purch-cert-editor__file-input"
                  accept="application/pdf,.pdf"
                  disabled={busy}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <span className="purch-cert-editor__file-btn" aria-hidden>
                  Choose PDF
                </span>
                <span className="purch-cert-editor__file-name">
                  {file?.name ?? 'No file chosen'}
                </span>
              </div>
            </label>
          </div>
          <div className="purch-cert-editor__actions">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={busy}
              onClick={closeEditor}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={busy}
              onClick={() => void saveUpload()}
            >
              {busy ? 'Saving…' : 'Save PDF'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        title={
          editSlot
            ? `Not available · ${editSlot.materialLabel} · ${CERTIFICATE_LIBRARY_TYPE_LABEL[editSlot.cert_type]}`
            : 'Not available'
        }
        open={editorMode === 'unavailable' && Boolean(editSlot)}
        onClose={closeEditor}
        nested
        compactHeader
        panelClassName="purch-cert-editor-modal"
      >
        <div className="purch-cert-editor">
          <p className="purch-cert-editor__hint">
            Mark this slot as not available. The reason is shown on posted Certificate Requests.
          </p>
          <label className="field">
            <span className="field-label">Reason</span>
            <textarea
              className="field-input field-textarea purch-cert-editor__reason"
              rows={4}
              value={naReason}
              disabled={busy}
              onChange={(e) => setNaReason(e.target.value)}
              placeholder="e.g. Supplier does not provide MSDS for this grade"
            />
          </label>
          <div className="purch-cert-editor__actions">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={busy}
              onClick={closeEditor}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={busy}
              onClick={() => void saveUnavailable()}
            >
              {busy ? 'Saving…' : 'Save not available'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
